import clsx from 'clsx'
import { ImageIcon, Upload, X } from 'lucide-react'
import { useRef, useState } from 'react'
import type { UploadResponse } from '@/types'
import { RequestError, api } from '@/lib/api'
import { Spinner } from './ui'

const MAX_BYTES = 5 * 1024 * 1024 // 5 MB (mismo límite que el edge)
const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml'

interface Props {
  label: string
  /** URL actual (puede ser /media/* subida o una URL externa pegada a mano). */
  value: string
  onChange: (url: string) => void
  hint?: string
  /** Placeholder del input de URL. */
  placeholder?: string
  /** Clases del preview (default: cuadrado 16). */
  previewClassName?: string
}

/**
 * Campo de imagen: sube un archivo a R2 (§7.5 / §7.6) o pega una URL externa.
 * Guarda siempre un string en `value` (la URL), compatible con los campos que
 * ya existían "por URL".
 */
export function ImageUploadField({ label, value, onChange, hint, placeholder, previewClassName }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = '' // permite re-subir el mismo archivo
    if (!file) return
    setErr(null)
    if (file.size > MAX_BYTES) {
      setErr('La imagen supera los 5 MB.')
      return
    }
    setBusy(true)
    try {
      const res = await api.upload<UploadResponse>('/uploads', file)
      onChange(res.url)
    } catch (e2) {
      setErr(e2 instanceof RequestError ? e2.message : 'No se pudo subir la imagen.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <label className="field-label">{label}</label>
      <div className="flex items-center gap-3">
        <div
          className={clsx(
            'flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-line bg-surface-sunken',
            previewClassName || 'h-16 w-16'
          )}
        >
          {value ? (
            <img src={value} alt="" className="h-full w-full object-contain" />
          ) : (
            <ImageIcon className="h-5 w-5 text-ink-faint" aria-hidden />
          )}
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary py-2 text-xs"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
            >
              {busy ? <Spinner className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5" />}
              {value ? 'Cambiar' : 'Subir'}
            </button>
            {value && (
              <button
                type="button"
                className="btn-ghost py-2 text-xs text-danger"
                onClick={() => onChange('')}
                disabled={busy}
              >
                <X className="h-3.5 w-3.5" /> Quitar
              </button>
            )}
          </div>
          <input
            className="field-input py-1.5 text-xs"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || 'o pegá una URL…'}
          />
        </div>
      </div>
      <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={onPick} />
      {hint && <p className="mt-1 text-xs text-ink-faint">{hint}</p>}
      {err && <p className="mt-1 text-xs text-danger">{err}</p>}
    </div>
  )
}
